import { memo, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { Heart, ImagePlay, Search, Smile } from 'lucide-react'
import { Avatar, IconButton, Notice, Popover, TextField } from '@/components/play'
import { watchApi } from '@/lib/watch/api'
import { initialsSource, nameOrHandle, profileAvatarUrl } from '@/lib/watch/rules'
import { useLike, useTypers, type Typer } from '@/lib/watch/stagePulse'
import { personToneKey } from '@/lib/profile'
import type { ChatGif, ChatMessage } from '@/lib/watch/types'
import { cn } from '@/lib/utils'
import './watch-together.css'

// ── Time ──

/** One clock for every message's time: it ticks every 30 s while anything shows a time, and not at all otherwise. */
const clock = (() => {
  let now = Date.now()
  let timer: ReturnType<typeof setInterval> | undefined
  const listeners = new Set<() => void>()
  return {
    subscribe(listener: () => void) {
      listeners.add(listener)
      if (!timer) {
        now = Date.now()
        timer = setInterval(() => {
          now = Date.now()
          listeners.forEach((each) => each())
        }, 30_000)
      }
      return () => {
        listeners.delete(listener)
        if (listeners.size === 0) {
          clearInterval(timer)
          timer = undefined
        }
      }
    },
    now: () => now,
  }
})()

const clockTime = (at: Date) => at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

/**
 * How long ago, as a chat says it with numbers: `now` under a minute, `4m` under an hour, then the clock time (`14:32`),
 * and the day before it past today (`Mon 14:32`). Null for a time that doesn't parse.
 */
export function chatTime(createdAt: string, now = Date.now()): string | null {
  const at = new Date(createdAt)
  const ms = at.getTime()
  if (Number.isNaN(ms)) return null
  const minutes = Math.floor((now - ms) / 60_000)
  if (minutes < 1) return 'now'
  if (minutes < 60) return `${minutes}m`
  if (new Date(now).toDateString() === at.toDateString()) return clockTime(at)
  return `${at.toLocaleDateString([], { weekday: 'short' })} ${clockTime(at)}`
}

/** A message's time, quiet at the line's end; the full date and time in its tooltip and for screen readers. */
export const ChatTime = memo(function ChatTime({ createdAt }: { createdAt: string }) {
  const now = useSyncExternalStore(clock.subscribe, clock.now)
  const short = chatTime(createdAt, now)
  if (!short) return null
  const full = new Date(createdAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
  return (
    <time className="watch-chat__time" dateTime={createdAt} title={full}>
      <span aria-hidden="true">{short}</span>
      <span className="sr-only">{full}</span>
    </time>
  )
})

// ── Likes ──

/**
 * A message's heart: shown on hover or focus, and always once someone likes it, with how many. Filled pink when this
 * viewer likes it. A press likes or unlikes (signed out, it asks to sign in first).
 */
export const LikeButton = memo(function LikeButton({ message, onToggle }: { message: ChatMessage; onToggle: (message: ChatMessage) => void }) {
  const { likes, liked } = useLike(message)
  const label = liked ? 'Unlike' : 'Like'
  return (
    <button
      type="button"
      className="watch-chat__like"
      data-liked={liked || undefined}
      data-count={likes > 0 || undefined}
      aria-pressed={liked}
      aria-label={likes > 0 ? `${label}, ${likes} ${likes === 1 ? 'like' : 'likes'}` : label}
      title={label}
      onClick={() => onToggle(message)}
    >
      <Heart aria-hidden="true" />
      {likes > 0 && <span aria-hidden="true">{likes}</span>}
    </button>
  )
})

// ── Typing ──

function typingWords(typers: readonly Typer[]) {
  const names = typers.map(({ person }) => nameOrHandle(person.name, person.handle, 'Someone'))
  if (names.length === 1) return `${names[0]} is typing`
  if (names.length === 2) return `${names[0]} and ${names[1]} are typing`
  return 'Several people are typing'
}

/**
 * Who is typing, just above the composer: their faces (up to three) and the dots; the words in a polite live region
 * for screen readers, and the first name beside the dots when one person is. Takes no room when nobody types.
 */
export const TypingLine = memo(function TypingLine() {
  const typers = useTypers()
  const words = typers.length ? typingWords(typers) : ''
  return (
    <div className="watch-chat__typing" data-active={typers.length > 0 || undefined}>
      <span className="sr-only" aria-live="polite">
        {words}
      </span>
      {typers.length > 0 && (
        <span className="watch-chat__typing-line" aria-hidden="true">
          <span className="watch-chat__typing-faces">
            {typers.slice(0, 3).map(({ person }) => (
              <Avatar
                key={person.id}
                name={initialsSource(person.name, person.handle)}
                src={profileAvatarUrl(person.avatar_url)}
                size="xs"
                tone="auto"
                toneKey={personToneKey(person)}
              />
            ))}
          </span>
          <span className="watch-chat__typing-dots">
            <i />
            <i />
            <i />
          </span>
          {typers.length === 1 && <bdi className="watch-chat__typing-name">{nameOrHandle(typers[0].person.name, typers[0].person.handle, '')}</bdi>}
        </span>
      )}
    </div>
  )
})

// ── Emoji ──

/** The picker's emoji: the ones a race chat reaches for, a few rows of each feeling. */
export const CHAT_EMOJI = [
  '😀', '😂', '🥹', '😍', '🤩', '😮', '😅', '😬', '🥵', '🥶', '😭', '🤯',
  '👏', '🙌', '👍', '💪', '🫡', '🤞', '👊', '✌️', '🙏', '👀', '❤️', '💛',
  '🔥', '⚡', '💦', '🎉', '📣', '🏅', '🥇', '🏆', '🏁', '⏱️', '🎯', '💯',
  '🏃', '🏃‍♀️', '🚴', '🚴‍♀️', '🏊', '🥾', '⛰️', '🏔️', '🌄', '☀️', '🌧️', '🌬️',
  '🍌', '🥤', '🍝', '🧂', '🦵', '🫁', '😵‍💫', '🐌', '🚀', '🐐', '🦄', '🐍',
] as const

/**
 * The composer's emoji button: a small grid in Play's popover. A press puts the emoji in the message at the caret and
 * keeps the grid open for another; Escape or a press outside closes it.
 */
export function EmojiPicker({ onPick, disabled }: { onPick: (emoji: string) => void; disabled?: boolean }) {
  return (
    <Popover
      trigger={
        <IconButton variant="ghost" size="compact" label="Emoji" className="watch-chat__tool" disabled={disabled}>
          <Smile />
        </IconButton>
      }
      label="Emoji"
      side="top"
      align="start"
      className="watch-chat__emoji-popover"
      initialFocus={false}
    >
      <div className="watch-chat__emoji-grid" role="group" aria-label="Emoji">
        {CHAT_EMOJI.map((emoji) => (
          <button key={emoji} type="button" className="watch-chat__emoji" onClick={() => onPick(emoji)}>
            {emoji}
          </button>
        ))}
      </div>
    </Popover>
  )
}

// ── GIFs ──

const SEARCH_DELAY_MS = 300

type GifResults = { state: 'loading' } | { state: 'error' } | { state: 'ready'; gifs: ChatGif[] }

/**
 * The composer's GIF button (only while the API has GIPHY): a search over trending GIFs in Play's popover. A press
 * sends the GIF as its own message and closes the picker. GIPHY's attribution sits at the foot, as its terms ask.
 */
export function GifPicker({ onPick, disabled }: { onPick: (gif: ChatGif) => void; disabled?: boolean }) {
  const [open, setOpen] = useState(false)
  return (
    <Popover
      trigger={
        <IconButton variant="ghost" size="compact" label="GIF" className="watch-chat__tool" disabled={disabled}>
          <ImagePlay />
        </IconButton>
      }
      label="GIFs"
      side="top"
      align="start"
      open={open}
      onOpenChange={setOpen}
      className="watch-chat__gif-popover"
    >
      {open && (
        <GifSearch
          onPick={(gif) => {
            setOpen(false)
            onPick(gif)
          }}
        />
      )}
    </Popover>
  )
}

function GifSearch({ onPick }: { onPick: (gif: ChatGif) => void }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<GifResults>({ state: 'loading' })
  const asked = useRef(0)

  useEffect(() => {
    const ask = ++asked.current
    setResults((current) => (current.state === 'ready' ? current : { state: 'loading' }))
    const timer = setTimeout(
      () => {
        watchApi.gifs(query.trim()).then(
          ({ data }) => {
            if (ask === asked.current) setResults({ state: 'ready', gifs: Array.isArray(data) ? data : [] })
          },
          () => {
            if (ask === asked.current) setResults({ state: 'error' })
          },
        )
      },
      query ? SEARCH_DELAY_MS : 0,
    )
    return () => clearTimeout(timer)
  }, [query])

  return (
    <div className="watch-chat__gifs">
      <TextField
        label="Search GIFs"
        hideLabel
        icon={<Search />}
        size="compact"
        placeholder="Search GIFs"
        autoComplete="off"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      <div className="watch-chat__gif-grid" aria-busy={results.state === 'loading' || undefined}>
        {results.state === 'error' ? (
          <Notice tone="error" aria-live="off">
            GIFs aren’t available right now.
          </Notice>
        ) : results.state === 'ready' ? (
          results.gifs.length ? (
            results.gifs.map((gif) => (
              <button key={gif.id} type="button" className="watch-chat__gif-option" onClick={() => onPick(gif)} aria-label={gif.title || 'GIF'}>
                <img src={gif.url} width={gif.width} height={gif.height} alt="" loading="lazy" decoding="async" />
              </button>
            ))
          ) : (
            <p className="watch-chat__gif-empty">No GIFs found</p>
          )
        ) : null}
      </div>
      <p className="watch-chat__giphy">Powered by GIPHY</p>
    </div>
  )
}

/** A GIF in a message, at its own shape, held to the chat's width and a modest height. */
export function ChatGifImage({ gif, className }: { gif: ChatGif; className?: string }) {
  return (
    <img
      className={cn('watch-chat__gif', className)}
      src={gif.url}
      width={gif.width}
      height={gif.height}
      alt={gif.title ? `GIF: ${gif.title}` : 'GIF'}
      loading="lazy"
      decoding="async"
    />
  )
}
